import React from "react";
import { SectionRule } from "components/common/SectionRule";
import { Tooltip } from "@/components/common/Tooltip";
import { plural } from "components/common/ImpactRow";
import { formatShortDate } from "@/lib/dates";
import { formatBytes } from "@/lib/format";
import type { OrgUsage } from "@/lib/api/organizations";
import { UsageRow } from "components/main/organizations/UsageRow";

const countOf = (value: number) => value.toLocaleString();

export const UsageSection: React.FC<{
  usage: OrgUsage | null;
  onRefresh: () => void;
}> = ({ usage, onRefresh }) => {
  const resetCountdown = (() => {
    if (!usage) return null;
    const on = formatShortDate(usage.egress.resets_at * 1000);
    const left = Math.ceil(
      (usage.egress.resets_at - Date.now() / 1000) / 86_400,
    );
    return {
      label:
        left <= 0
          ? "Resets today"
          : `Resets ${on} · ${left} ${plural(left, "day")}`,
      on,
    };
  })();

  return (
    <section className="flex flex-col">
      <SectionRule
        label="Usage"
        count={
          resetCountdown && (
            <Tooltip title="Egress and tool calls start over each billing month. Storage is a running total.">
              <span className="cursor-help" data-testid="usage-resets">
                {resetCountdown.label}
              </span>
            </Tooltip>
          )
        }
        action={{
          label: "Refresh",
          onClick: onRefresh,
          testId: "refresh-usage",
        }}
      />
      {usage && (
        <>
          <UsageRow
            label="Storage"
            info="Data held by this organization's tables. A table imported from elsewhere counts against its owner, not you."
            meter={usage.storage}
            format={formatBytes}
            spentNote="Storage is full. Uploads and edits are refused until space is freed."
          />
          <UsageRow
            label="Egress"
            info="Data leaving MyCorr: exports, downloads, the API and AI tool results. Reading inside the app is free."
            meter={usage.egress}
            format={formatBytes}
            spentNote={`This month's egress allowance is spent. Exports and API reads are refused until ${formatShortDate(usage.egress.resets_at * 1000)}.`}
          />
          <UsageRow
            label="MCP tool calls"
            info="Tool calls an AI assistant made against this organization's data. What those calls return also counts as egress."
            meter={usage.mcp_calls}
            format={countOf}
            spentNote={`This month's tool calls are used up. They reset ${formatShortDate(usage.mcp_calls.resets_at * 1000)}.`}
          />
        </>
      )}
    </section>
  );
};
